import { useNavigate } from 'react-router';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { UniversalBulkImportResponse } from 'store/services/import/importApi';
import MagicButton from 'components/MagicButton';

interface ImportSuccessProps {
  result: UniversalBulkImportResponse | null;
  partner?: string;
}

const ImportSuccess = ({ result, partner }: ImportSuccessProps) => {
  const navigate = useNavigate();

  if (!result) {
    return null;
  }

  const hasErrors = result.errorCount > 0;
  const allFailed = result.successCount === 0 && result.errorCount > 0;

  return (
    <div className="import-success">
      <div className="import-success-header">
        <FontAwesomeIcon
          icon={allFailed ? faExclamationTriangle : faCheckCircle}
          className={`import-success-icon ${allFailed ? 'error' : 'success'}`}
        />
        <h2>{allFailed ? 'Import Failed' : 'Import Complete!'}</h2>
      </div>

      <div className="import-success-stats">
        <div className="stat-item success">
          <span className="stat-value">{result.successCount.toLocaleString()}</span>
          <span className="stat-label">Cards Imported</span>
        </div>
        {hasErrors && (
          <div className="stat-item error">
            <span className="stat-value">{result.errorCount.toLocaleString()}</span>
            <span className="stat-label">Failed</span>
          </div>
        )}
      </div>

      {hasErrors && result.errors.length > 0 && (
        <div className="import-errors-section">
          <h3>Import Errors</h3>
          <div className="import-errors-list">
            {result.errors.slice(0, 10).map((error, index) => (
              <div key={index} className="import-error-item">
                <span className="error-card-name">{error.cardName || error.scryfallUuid}</span>
                <span className="error-message">{error.error}</span>
              </div>
            ))}
            {result.errors.length > 10 && (
              <p className="more-errors">
                ...and {result.errors.length - 10} more errors
              </p>
            )}
          </div>
        </div>
      )}

      {!allFailed && (
        <div className="import-success-next-steps">
          <h3>What's Next?</h3>
          <div className="next-steps-list">
            <button
              className="next-step-btn"
              onClick={() => navigate('/my-cards/want-list')}
              type="button"
            >
              <span className="next-step-icon">+</span>
              <div className="next-step-content">
                <span className="next-step-title">Build Your Want List</span>
                <span className="next-step-description">
                  Add cards you're looking for to find trades
                </span>
              </div>
            </button>
            <button
              className="next-step-btn"
              onClick={() => navigate('/my-cards/have-list')}
              type="button"
            >
              <span className="next-step-icon">&#10003;</span>
              <div className="next-step-content">
                <span className="next-step-title">View Your Collection</span>
                <span className="next-step-description">
                  Review imported cards and edit conditions
                </span>
              </div>
            </button>
          </div>
        </div>
      )}

      <div className="import-success-actions">
        <MagicButton
          label={allFailed ? 'Try Again' : 'Go to Collection'}
          color="purple"
          onPress={() => navigate(allFailed ? `/import/${partner || ''}` : '/my-cards/have-list')}
        />
      </div>
    </div>
  );
};

export default ImportSuccess;
